import { Fragment, useState, type ReactNode, type CSSProperties } from 'react';
import { ChevronDown } from 'lucide-react';
import { Footer } from '@/components/sections/Footer';
import { PageBreadcrumb } from '@/components/sections/PageBreadcrumb';
import { usePrerenderReady } from '@/hooks/usePrerenderReady';
import { useSeo } from '@/lib/useSeo';
import { CmsEditorialText } from '@/components/cms/CmsEditorialText';
import { CmsLegalBlocks } from '@/components/cms/CmsLegalBlocks';
import type { ContentBlock } from '@/lib/blogContentBlocks';

/**
 * Wspólny szablon stron prawnych/informacyjnych (Regulamin, Polityka
 * prywatności, Polityka zwrotów) — wydzielony z trzech niemal identycznych
 * plików bez zmiany wyglądu żadnej z nich. Dwie różnice, które dziś istnieją
 * między stronami (spis treści statyczny vs zwijany na mobile; separator
 * artykułów w formie kropki vs górnej linii) są zachowane jako warianty
 * configu (`tocVariant`, `articleSeparator`), a nie ujednolicone na siłę.
 */

/* ─── Tokens (wspólne dla stron prawnych) ───────────────────── */
export const GOLD = '#e05590';
export const DARK = '#2a1f14';
export const MUTED = '#7a7068';
export const LINE = 'rgba(210,60,120,0.18)';
export const CREAM = '#fff';
export const CREAM2 = '#fff';

/* ─── Shared prose styles ────────────────────────────────────── */
export const proseStyle: CSSProperties = {
  fontFamily: 'DM Sans, sans-serif',
  fontSize: 15,
  lineHeight: 1.8,
  color: DARK,
};

export const h2Style: CSSProperties = {
  fontFamily: 'Cormorant Garamond, Georgia, serif',
  fontSize: 'clamp(1.35rem, 2.5vw, 1.75rem)',
  fontWeight: 500,
  color: DARK,
  margin: '0 0 18px',
  lineHeight: 1.25,
  letterSpacing: '0.01em',
};

export const pStyle: CSSProperties = { margin: '0 0 14px', ...proseStyle };
export const liStyle: CSSProperties = { marginBottom: 8, ...proseStyle };

export const noteStyle: CSSProperties = {
  fontFamily: 'DM Sans, sans-serif',
  fontSize: 13,
  lineHeight: 1.7,
  color: MUTED,
  background: CREAM2,
  border: `1px solid ${LINE}`,
  padding: '12px 16px',
  margin: '0 0 14px',
};

export const linkStyle: CSSProperties = {
  color: GOLD,
  textDecoration: 'none',
  borderBottom: `1px solid ${LINE}`,
};

/* ─── Dekorator między artykułami (wariant "dots") ───────────── */

/* ─── Podświetlenie frazy w tekście (np. "14 dni") ───────────── */
export function Hi({ children }: { children: ReactNode }) {
  return (
    <strong style={{ fontWeight: 600, color: DARK, fontFamily: 'Cormorant Garamond, serif', fontSize: '1.05em' }}>
      {children}
    </strong>
  );
}

/**
 * Styling for `ContentBlock[]` article bodies — the semantic-HTML equivalent
 * of pStyle/liStyle/linkStyle/Hi() above, mirroring how blog-artykul.tsx's
 * styledContent() re-applies typography to plain HTML tags. `class="legal-note"`
 * (noteStyle callouts) and `<strong data-hi="true">` (the former `Hi` component)
 * are semantic markers produced by the JSX→HTML conversion — see regulamin.tsx
 * / prywatnosc.tsx / polityka-zwrotow.tsx for where each body was converted.
 */
const LEGAL_P_STYLE = `margin:0 0 14px;font-family:'DM Sans',sans-serif;font-size:15px;line-height:1.8;color:${DARK}`;
const LEGAL_NOTE_STYLE = `font-family:'DM Sans',sans-serif;font-size:13px;line-height:1.7;color:${MUTED};background:${CREAM2};border:1px solid ${LINE};padding:12px 16px;margin:0 0 14px`;
const LEGAL_MUTED_P_STYLE = `margin:0;font-family:'DM Sans',sans-serif;font-size:14px;line-height:1.8;color:${MUTED}`;
const LEGAL_LIST_STYLE = `margin:0 0 14px;padding-left:24px`;
const LEGAL_LI_STYLE = `margin-bottom:8px;font-family:'DM Sans',sans-serif;font-size:15px;line-height:1.8;color:${DARK}`;
const LEGAL_LINK_STYLE = `color:#bf3d75;text-decoration:none;border-bottom:1px solid ${LINE}`;
const LEGAL_HI_STYLE = `font-weight:600;color:${DARK};font-family:'Cormorant Garamond',serif;font-size:1.05em`;
const LEGAL_STRONG_STYLE = `font-weight:600;color:${DARK}`;

function styleLegalHtml(html: string): string {
  return html
    .replace(/<p class="legal-note">/g, `<p style="${LEGAL_NOTE_STYLE}">`)
    .replace(/<p class="legal-muted">/g, `<p style="${LEGAL_MUTED_P_STYLE}">`)
    .replace(/<p>/g, `<p style="${LEGAL_P_STYLE}">`)
    .replace(/<ul>/g, `<ul style="${LEGAL_LIST_STYLE}">`)
    .replace(/<ol>/g, `<ol style="${LEGAL_LIST_STYLE}">`)
    .replace(/<li>/g, `<li style="${LEGAL_LI_STYLE}">`)
    .replace(/<a /g, `<a style="${LEGAL_LINK_STYLE}" `)
    .replace(/<strong data-hi="true">/g, `<strong style="${LEGAL_HI_STYLE}">`)
    .replace(/<strong>/g, `<strong style="${LEGAL_STRONG_STYLE}">`);
}

function styleLegalBlocks(blocks: ContentBlock[]): ContentBlock[] {
  return blocks.map(block => ({ ...block, html: styleLegalHtml(block.html) }));
}

function LegalArticleBody({ body, articleId, title }: { body: LegalArticleConfig['body']; articleId: string; title: string }) {
  return Array.isArray(body) ? <CmsLegalBlocks blocks={styleLegalBlocks(body)} articleId={articleId} title={title} /> : <>{body}</>;
}

export interface LegalTocItem {
  id: string;
  label: string;
}

/* ─── Spis treści, wariant "static" (Regulamin, Prywatność) ──── */
function LegalTocStatic({ items, stripNumberPrefix }: { items: LegalTocItem[]; stripNumberPrefix?: boolean }) {
  return (
    <nav aria-label="Spis treści" style={{
      background: '#fff',
      border: `1px solid ${LINE}`,
      padding: 'clamp(20px, 3vw, 28px) clamp(20px, 3vw, 32px)',
      marginBottom: 'clamp(40px, 5vw, 64px)',
    }}>
      <p style={{ fontFamily: 'DM Sans, sans-serif', fontSize: 10, letterSpacing: '0.28em', textTransform: 'uppercase', color: GOLD, marginBottom: 14 }}>
        Spis treści
      </p>
      <ol style={{ margin: 0, padding: '0 0 0 20px', listStyleType: 'decimal' }}>
        {items.map(({ id, label }) => (
          <li key={id} style={{ ...liStyle, marginBottom: 6 }}>
            <a href={`#${id}`} style={{ color: DARK, textDecoration: 'none', borderBottom: `1px solid ${LINE}`, paddingBottom: 1, transition: 'color 0.15s, border-color 0.15s' }}
              onMouseEnter={e => { (e.currentTarget as HTMLAnchorElement).style.color = GOLD; (e.currentTarget as HTMLAnchorElement).style.borderBottomColor = GOLD; }}
              onMouseLeave={e => { (e.currentTarget as HTMLAnchorElement).style.color = DARK; (e.currentTarget as HTMLAnchorElement).style.borderBottomColor = LINE; }}>
              {stripNumberPrefix ? label.replace(/^\d+\.\s/, '') : label}
            </a>
          </li>
        ))}
      </ol>
    </nav>
  );
}

/* ─── Spis treści, wariant "collapsible" (Polityka zwrotów) ──── */
function LegalTocCollapsible({ items }: { items: LegalTocItem[] }) {
  const [open, setOpen] = useState(false);

  const list = (
    <ol style={{ margin: '12px 0 0', padding: '0 0 0 20px' }}>
      {items.map(t => (
        <li key={t.id} style={{ marginBottom: 7 }}>
          <a
            href={`#${t.id}`}
            style={{ fontFamily: 'DM Sans, sans-serif', fontSize: 14, color: DARK, textDecoration: 'none', lineHeight: 1.5 }}
            onMouseEnter={e => (e.currentTarget.style.color = GOLD)}
            onMouseLeave={e => (e.currentTarget.style.color = DARK)}
          >
            {t.label}
          </a>
        </li>
      ))}
    </ol>
  );

  return (
    <nav aria-label="Spis treści" style={{ background: '#fff', border: `1px solid ${LINE}`, padding: 'clamp(20px,3vw,28px) clamp(20px,3vw,32px)', marginBottom: 'clamp(40px,5vw,60px)' }}>
      <p style={{ fontFamily: 'DM Sans, sans-serif', fontSize: 10, letterSpacing: '0.28em', textTransform: 'uppercase', color: GOLD, margin: 0 }}>
        Spis treści
      </p>

      {/* Desktop: always visible */}
      <div className="hidden md:block">{list}</div>

      {/* Mobile: toggle */}
      <div className="md:hidden">
        <button
          onClick={() => setOpen(v => !v)}
          aria-expanded={open}
          style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 10, background: 'none', border: 'none', cursor: 'pointer', padding: 0, fontFamily: 'DM Sans, sans-serif', fontSize: 13, color: MUTED }}
        >
          {open ? 'Zwiń' : 'Rozwiń'}
          <ChevronDown size={13} style={{ transition: 'transform 0.2s', transform: open ? 'rotate(180deg)' : 'rotate(0deg)' }} />
        </button>
        {open && list}
      </div>
    </nav>
  );
}

export interface LegalArticleConfig {
  id: string;
  title: string;
  /**
   * `ContentBlock[]` for plain editorial content (CMS-ready — see
   * blogContentBlocks.ts) or `ReactNode` for the rare article that embeds a
   * live component (e.g. prywatnosc.tsx's "cookies" article, which renders
   * `<CookieSettingsButton>` — that one stays JSX since a static HTML block
   * can't represent an interactive control).
   */
  body: ContentBlock[] | ReactNode;
}

export interface LegalPageConfig {
  seo: { title: string; description: string; robots?: string };
  breadcrumbLabel: string;
  heroTitle: string;
  /** Cały blok podtytułu pod H1 — każda strona ma dziś inny styl/treść. */
  heroSubtitle: ReactNode;
  /** Akapit wstępny nad spisem treści (dziś tylko Polityka prywatności). */
  intro?: ReactNode;
  toc: LegalTocItem[];
  tocVariant: 'static' | 'collapsible';
  /** Tylko dla wariantu 'static' — Regulamin czyści numerację z etykiety, bo <ol> już numeruje. */
  tocStripNumberPrefix?: boolean;
  /** 'dots' = belka akcentu + kropkowany divider (Regulamin, Prywatność).
   *  'border' = górna linia nad każdą sekcją, bez belki (Polityka zwrotów). */
  articleSeparator: 'dots' | 'border';
  articles: LegalArticleConfig[];
}

export function LegalPageTemplate({ config }: { config: LegalPageConfig }) {
  useSeo(config.seo);
  usePrerenderReady();

  return (
    <div style={{ background: '#fff', minHeight: '100vh' }}>
      <PageBreadcrumb crumbs={[{ label: config.breadcrumbLabel }]} />

      {/* ── Page hero ─────────────────────────────────────────── */}
      <section style={{ background: '#fff', borderBottom: `1px solid ${LINE}`, padding: 'max(clamp(56px, 7vw, 88px), calc(var(--page-breadcrumb-height, 44px) + 12px)) 0 clamp(40px, 5vw, 64px)' }}>
        <div style={{ maxWidth: 760, margin: '0 auto', padding: '0 clamp(20px, 5vw, 48px)', textAlign: 'center' }}>
          <p style={{ fontFamily: 'DM Sans, sans-serif', fontSize: 10, letterSpacing: '0.32em', textTransform: 'uppercase', color: GOLD, marginBottom: 16 }}>
            Bukietowa
          </p>
          <h1 style={{ fontFamily: 'Cormorant Garamond, Georgia, serif', fontSize: 'clamp(2.4rem, 5vw, 3.6rem)', fontWeight: 400, color: DARK, margin: '0 0 20px', lineHeight: 1.1 }}>
            <CmsEditorialText id="legal.heroTitle" value={config.heroTitle} section="Wstęp" label="Nagłówek strony" />
          </h1>
          {config.heroSubtitle}
        </div>
      </section>

      {/* ── Main content ──────────────────────────────────────── */}
      <section style={{ padding: 'clamp(48px, 6vw, 80px) 0 clamp(64px, 8vw, 120px)' }}>
        <div style={{ maxWidth: 760, margin: '0 auto', padding: '0 clamp(20px, 5vw, 48px)' }}>

          {config.intro}

          {config.tocVariant === 'static'
            ? <LegalTocStatic items={config.toc} stripNumberPrefix={config.tocStripNumberPrefix} />
            : <LegalTocCollapsible items={config.toc} />}

          {config.articleSeparator === 'dots'
            ? config.articles.map((article, i) => (
              <Fragment key={article.id}>
                <article id={article.id} style={{ marginBottom: i === config.articles.length - 1 ? 0 : 'clamp(40px, 5vw, 64px)', scrollMarginTop: 96 }}>
                  <h2 style={h2Style}><CmsEditorialText id={`legal.${article.id}.title`} value={article.title} section={article.title} label="Nagłówek sekcji" /></h2>

                  <LegalArticleBody body={article.body} articleId={article.id} title={article.title} />
                </article>
              </Fragment>
            ))
            : config.articles.map(article => (
              <section key={article.id} id={article.id} style={{ paddingTop: 'clamp(36px,4vw,52px)', borderTop: `1px solid ${LINE}`, marginTop: 'clamp(36px,4vw,52px)' }}>
                <h2 style={h2Style}><CmsEditorialText id={`legal.${article.id}.title`} value={article.title} section={article.title} label="Nagłówek sekcji" /></h2>
                <LegalArticleBody body={article.body} articleId={article.id} title={article.title} />
              </section>
            ))}

        </div>
      </section>

      <Footer />
    </div>
  );
}
